import { useMemo, useState, useEffect } from "react";
import { Youtube, ExternalLink, ListChecks, FileText } from "lucide-react";

/* ---------- helpers ---------- */

function extractYouTubeId(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1).split(/[/?]/)[0] || null;
    if (u.hostname.includes("youtube.com")) {
      if (u.searchParams.get("v")) return u.searchParams.get("v");
      const parts = u.pathname.split("/").filter(Boolean);
      const idx = parts.findIndex((p) => p === "embed" || p === "shorts" || p === "v");
      if (idx !== -1 && parts[idx + 1]) return parts[idx + 1];
    }
    return null;
  } catch {
    return null;
  }
}

/** Very small inline-markdown → HTML. Handles: bold, italic, code, links, and bullet lists. */
function safeHref(url) {
  // Block javascript:, data:, vbscript: etc. Allow http(s), mailto, tel, and relative.
  const s = String(url || "").trim();
  if (/^\s*(javascript|data|vbscript|file):/i.test(s)) return "#";
  return s;
}

export function renderMiniMarkdown(md) {
  if (!md) return null;
  const lines = md.split("\n");
  const nodes = [];
  let list = null;          // <ul>
  let olist = null;         // <ol>
  let quote = null;         // <blockquote>
  let code = null;          // inside a ```fenced code block
  let codeLines = [];
  const flushList = () => { if (list) { nodes.push(list); list = null; } };
  const flushOList = () => { if (olist) { nodes.push(olist); olist = null; } };
  const flushQuote = () => { if (quote) { nodes.push(quote); quote = null; } };
  const flushAll = () => { flushList(); flushOList(); flushQuote(); };
  const flushCode = (key) => {
    if (code == null) return;
    nodes.push(
      <pre key={`code-${key}`} className="bg-slate-900 text-slate-100 border border-slate-200 p-3 overflow-x-auto text-[12px] font-mono-num rounded-none">
        <code>{codeLines.join("\n")}</code>
      </pre>
    );
    code = null;
    codeLines = [];
  };
  const inline = (text) =>
    text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/`([^`]+)`/g, '<code class="font-mono-num text-[#0D9488] bg-slate-50 px-1 py-0.5 border border-slate-200">$1</code>')
      .replace(/~~([^~]+)~~/g, '<s class="text-slate-500">$1</s>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-slate-900">$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em class="text-slate-900">$1</em>')
      .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_m, alt, url) =>
        `<img src="${safeHref(url)}" alt="${alt}" class="max-w-full h-auto my-2 border border-slate-200 rounded-none" loading="lazy" />`
      )
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, label, url) =>
        `<a href="${safeHref(url)}" target="_blank" rel="noopener noreferrer" class="text-[#0D9488] underline underline-offset-2 hover:text-[#0F766E]">${label}</a>`
      );
  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    // Fenced code block: opening or closing ``` on its own line
    if (/^```/.test(line.trim())) {
      if (code == null) {
        flushAll();
        code = "start";
        codeLines = [];
      } else {
        flushCode(i);
      }
      return;
    }
    if (code != null) { codeLines.push(raw); return; }
    // Horizontal divider
    if (/^---+\s*$/.test(line)) { flushAll(); nodes.push(<hr key={i} className="my-4 border-t border-slate-200" />); return; }
    // Custom YouTube embed token: @[video](https://youtube.com/...)
    const videoMatch = line.match(/^@\[video\]\(([^)]+)\)\s*$/);
    if (videoMatch) {
      flushAll();
      const url = videoMatch[1];
      const idMatch = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([A-Za-z0-9_-]{6,15})/);
      const vid = idMatch ? idMatch[1] : null;
      nodes.push(
        <div key={`v-${i}`} className="my-3 relative aspect-video border border-slate-200 bg-slate-900">
          {vid ? (
            <iframe
              src={`https://www.youtube.com/embed/${vid}`}
              title="Embedded video"
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <a href={safeHref(url)} target="_blank" rel="noopener noreferrer" className="absolute inset-0 flex items-center justify-center text-slate-100 text-sm">Open video</a>
          )}
        </div>
      );
      return;
    }
    if (line.startsWith("#### ")) { flushAll(); nodes.push(<h4 key={i} className="font-display font-black text-base uppercase tracking-tight text-slate-900 mt-3 mb-1">{line.slice(5)}</h4>); return; }
    if (line.startsWith("### ")) { flushAll(); nodes.push(<h3 key={i} className="font-display font-black text-lg uppercase tracking-tight text-slate-900 mt-4 mb-1">{line.slice(4)}</h3>); return; }
    if (line.startsWith("## ")) { flushAll(); nodes.push(<h2 key={i} className="font-display font-black text-xl uppercase tracking-tight text-slate-900 mt-5 mb-1.5">{line.slice(3)}</h2>); return; }
    if (line.startsWith("# ")) { flushAll(); nodes.push(<h1 key={i} className="font-display font-black text-2xl uppercase tracking-tight text-slate-900 mt-5 mb-2">{line.slice(2)}</h1>); return; }
    // Blockquote
    if (/^>\s+/.test(line)) {
      flushList(); flushOList();
      const content = line.replace(/^>\s+/, "");
      if (!quote) quote = <blockquote key={`q-${i}`} className="border-l-4 border-[#0D9488] bg-slate-50 pl-4 pr-3 py-2 italic text-slate-700 my-2 space-y-1" />;
      quote = { ...quote, props: { ...quote.props, children: [...(quote.props.children || []), <p key={i} className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(content) }} />] } };
      return;
    }
    // Numbered list
    if (/^\d+\.\s+/.test(line)) {
      flushList(); flushQuote();
      const item = line.replace(/^\d+\.\s+/, "");
      if (!olist) olist = <ol key={`ol-${i}`} className="list-decimal list-inside space-y-1 text-slate-800" />;
      olist = { ...olist, props: { ...olist.props, children: [...(olist.props.children || []), <li key={i} dangerouslySetInnerHTML={{ __html: inline(item) }} />] } };
      return;
    }
    // Bullet list
    if (/^[-*]\s+/.test(line)) {
      flushOList(); flushQuote();
      if (!list) list = <ul key={`ul-${i}`} className="list-disc list-inside space-y-1 text-slate-800" />;
      const item = line.replace(/^[-*]\s+/, "");
      list = { ...list, props: { ...list.props, children: [...(list.props.children || []), <li key={i} dangerouslySetInnerHTML={{ __html: inline(item) }} />] } };
      return;
    }
    flushAll();
    if (!line.trim()) { nodes.push(<div key={i} className="h-2" />); return; }
    nodes.push(<p key={i} className="text-sm leading-relaxed text-slate-800" dangerouslySetInnerHTML={{ __html: inline(line) }} />);
  });
  flushAll();
  if (code != null) flushCode("final");
  return nodes;
}

/* ---------- block ---------- */

export default function LessonBlock({ block, lessonId, index }) {
  const t = block?.type;
  if (t === "text") return <TextBlock text={block.text} />;
  if (t === "video") return <VideoBlock url={block.url} title={block.title} />;
  if (t === "link") return <LinkBlock url={block.url} title={block.title} description={block.description} />;
  if (t === "supplies") return <SuppliesBlock items={block.items} lessonId={lessonId} index={index} />;
  return null;
}

function TextBlock({ text }) {
  return (
    <div className="prose prose-slate max-w-none prose-headings:font-display prose-headings:font-black prose-headings:uppercase prose-headings:tracking-tight prose-headings:text-slate-900 prose-p:text-slate-800 prose-a:text-[#0D9488] prose-strong:text-slate-900 prose-code:text-[#0D9488] prose-li:text-slate-800" data-testid="lesson-block-text">
      {renderMiniMarkdown(text)}
    </div>
  );
}

function VideoBlock({ url, title }) {
  const id = useMemo(() => extractYouTubeId(url), [url]);
  return (
    <div data-testid="lesson-block-video" className="space-y-2">
      <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-[#0D9488] flex items-center gap-1.5">
        <Youtube className="w-3 h-3" /> // Video
      </div>
      {id ? (
        <div className="relative aspect-video border border-slate-200 bg-slate-900">
          <iframe
            src={`https://www.youtube.com/embed/${id}`}
            title={title || "YouTube video"}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : (
        <a href={url} target="_blank" rel="noopener noreferrer"
          className="block border border-slate-200 bg-slate-50 p-4 hover:border-[#0D9488] text-slate-800">
          <span className="text-xs text-slate-500">Unrecognized video URL — open externally:</span>
          <div className="text-[#0D9488] font-mono-num text-sm break-all">{url}</div>
        </a>
      )}
      {title && <div className="text-xs text-slate-500">{title}</div>}
    </div>
  );
}

function LinkBlock({ url, title, description }) {
  let host = url;
  try { host = new URL(url).host.replace(/^www\./, ""); } catch {}
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="group block border border-slate-200 hover:border-[#0D9488] bg-slate-50 p-4 transition-colors"
      data-testid="lesson-block-link"
    >
      <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-[#0D9488] mb-1 flex items-center gap-1.5">
        <ExternalLink className="w-3 h-3" /> {host}
      </div>
      <div className="font-display font-black text-lg uppercase tracking-tight text-slate-900 group-hover:text-[#0D9488] transition-colors">
        {title || url}
      </div>
      {description && <p className="text-sm text-slate-500 mt-1 leading-relaxed">{description}</p>}
    </a>
  );
}

function SuppliesBlock({ items, lessonId, index }) {
  const storageKey = `playbook:supplies:${lessonId || "x"}:${index || 0}`;
  const [checked, setChecked] = useState({});
  useEffect(() => {
    try { setChecked(JSON.parse(localStorage.getItem(storageKey) || "{}")); } catch {}
  }, [storageKey]);
  const toggle = (i) => {
    const next = { ...checked, [i]: !checked[i] };
    setChecked(next);
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch {}
  };
  const list = Array.isArray(items) ? items : [];
  const done = list.filter((_, i) => checked[i]).length;
  return (
    <div className="border border-slate-200 bg-slate-50 p-4" data-testid="lesson-block-supplies">
      <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-[#0D9488] mb-2 flex items-center justify-between">
        <span className="flex items-center gap-1.5"><ListChecks className="w-3 h-3" /> // Supplies</span>
        <span className="text-slate-500">{done} / {list.length}</span>
      </div>
      <ul className="space-y-1.5">
        {list.map((it, i) => (
          <li key={i} className="flex items-start gap-2 group">
            <button
              type="button"
              onClick={() => toggle(i)}
              className={`shrink-0 mt-0.5 w-4 h-4 border ${checked[i] ? "bg-[#0D9488] border-[#0D9488]" : "border-slate-300 hover:border-[#0D9488]"} flex items-center justify-center transition-colors`}
              data-testid={`supply-check-${i}`}
              aria-label={`Toggle ${it.name}`}
            >
              {checked[i] && <span className="text-white text-[10px] font-black leading-none">✓</span>}
            </button>
            <div className="min-w-0 flex-1">
              <div className={`text-sm ${checked[i] ? "text-slate-500 line-through" : "text-slate-900"}`}>
                {it.name}
              </div>
              {it.note && <div className="text-[11px] text-slate-500">{it.note}</div>}
              {it.url && (
                <a href={it.url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[10px] font-mono-num uppercase tracking-widest text-[#0D9488] hover:underline">
                  <ExternalLink className="w-2.5 h-2.5" /> buy link
                </a>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
